import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { LeadNote, CreateNoteData } from '@/types/lead.types';
import { leadsService } from '@/services/leads.service';
import { Button } from '@/components/ui/Button';

interface LeadNoteListProps {
  leadId: string;
}

export const LeadNoteList: React.FC<LeadNoteListProps> = ({ leadId }) => {
  const [notes, setNotes] = useState<LeadNote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [content, setContent] = useState('');

  useEffect(() => {
    fetchNotes();
  }, [leadId]);

  const fetchNotes = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await leadsService.getNotes(leadId);
      // API interceptor unwraps { success, data } - backend returns { notes: [...] }
      const notesData = response.data?.notes || response.data || [];
      setNotes(Array.isArray(notesData) ? notesData : []);
    } catch (err) {
      setError('Failed to load notes');
      console.error('Error fetching notes:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;

    try {
      setIsSubmitting(true);
      const data: CreateNoteData = { content: content.trim() };
      const response = await leadsService.addNote(leadId, data);
      // API interceptor unwraps { success, data } - backend returns { note: {...} }
      const newNote = response.data?.note || response.data;
      if (newNote) {
        setNotes([newNote, ...notes]);
      }
      setContent('');
      setShowAddForm(false);
    } catch (err) {
      console.error('Error adding note:', err);
      toast.error('Failed to add note');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center space-y-3">
          <div className="w-8 h-8 border-3 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-sm text-secondary-500">Loading notes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4">
          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-secondary-600">{error}</p>
        <button
          onClick={fetchNotes}
          className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Add Note Button / Form */}
      {!showAddForm ? (
        <Button
          variant="outline"
          onClick={() => setShowAddForm(true)}
          leftIcon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          }
        >
          Add Note
        </Button>
      ) : (
        <form onSubmit={handleSubmit} className="bg-secondary-50 rounded-lg p-4 space-y-4">
          <div>
            <label htmlFor="note-content" className="block text-sm font-medium text-secondary-700 mb-1.5">
              Note
            </label>
            <textarea
              id="note-content"
              rows={4}
              className="block w-full rounded-lg border border-secondary-300 bg-white px-4 py-2.5 text-secondary-900 placeholder-secondary-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-primary-500 focus:ring-primary-500 resize-none"
              placeholder="Write your note here..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              required
            />
          </div>
          <div className="flex items-center gap-3">
            <Button type="submit" isLoading={isSubmitting}>
              Add Note
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setShowAddForm(false);
                setContent('');
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Notes List */}
      {notes.length > 0 ? (
        <ul className="space-y-4">
          {notes.map((note) => (
            <li
              key={note.id}
              className="bg-white border border-secondary-200 rounded-lg p-4"
            >
              <div className="flex items-start gap-3">
                {/* Avatar */}
                <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-primary-700">
                    {getInitials(note.created_by.full_name)}
                  </span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-secondary-900">
                      {note.created_by.full_name}
                    </p>
                    <p className="text-xs text-secondary-500">
                      {formatDateTime(note.created_at)}
                    </p>
                  </div>
                  <p className="text-sm text-secondary-700 whitespace-pre-wrap">
                    {note.content}
                  </p>

                  {/* Attachment */}
                  {note.attachment_url && (
                    <a
                      href={note.attachment_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-3 text-sm text-primary-600 hover:text-primary-700"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      View Attachment
                    </a>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary-100 mb-4">
            <svg className="w-6 h-6 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </div>
          <p className="text-secondary-600">No notes yet</p>
          <p className="text-sm text-secondary-500 mt-1">Add a note to get started</p>
        </div>
      )}
    </div>
  );
};

export default LeadNoteList;
